import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ServicesPage() {
  const services = [
    {
      number: '01',
      title: 'Compliance & Safety Program Foundations',
      value: 'Get compliant, stay compliant, and build the foundation for everything else.',
      included: [
        'Complete OSHA compliance audit and gap analysis',
        'Written safety program development and documentation',
        'Required training program setup and delivery',
        'Injury and illness recordkeeping systems (OSHA 300 logs)',
        'Emergency action plans and evacuation procedures',
        'Hazard communication and chemical management programs'
      ],
      bestFor: 'Growing businesses, new facilities, or organizations needing to get their safety house in order.',
      industries: ['Manufacturing', 'Warehousing', 'Food Production', 'Construction']
    },
    {
      number: '02',
      title: 'The Forward Safety System™',
      value: 'Move beyond compliance to build a proactive safety culture that prevents incidents.',
      included: [
        'Five-pillar safety culture assessment and roadmap',
        'Leadership alignment workshops and coaching',
        'Critical behavior identification and observation tools',
        'Custom leading indicator scorecard development',
        'Risk recognition training for frontline teams',
        'Continuous improvement framework and quarterly reviews'
      ],
      bestFor: 'Organizations ready to move from reactive to proactive safety management.',
      industries: ['All Industries', 'Multi-site Operations', 'High-risk Environments']
    },
    {
      number: '03',
      title: 'Ongoing Safety Leadership Support',
      value: 'Keep your safety system strong with consistent coaching and accountability.',
      included: [
        'Monthly leadership coaching calls (1-2 hours)',
        'Leading indicator scorecard reviews and analysis',
        'Incident investigation support and root cause coaching',
        'Quarterly on-site visits (optional add-on)',
        'Access to safety tools, templates, and resources',
        'Email and phone support between scheduled sessions'
      ],
      bestFor: 'Organizations with safety programs in place who need ongoing guidance and accountability.',
      industries: ['Established Programs', 'Growing Teams', 'Multi-location Operations']
    },
    {
      number: '04',
      title: 'Fractional Safety Leadership',
      value: 'Part-time safety director support without the full-time cost.',
      included: [
        'Dedicated safety director hours (8-20 hours/month)',
        'Strategic safety planning and program oversight',
        'Regulatory compliance monitoring and updates',
        'Leadership team collaboration and reporting',
        'Incident management and investigation leadership',
        'Vendor management and contractor safety coordination'
      ],
      bestFor: 'Small to mid-sized organizations not ready for a full-time safety director.',
      industries: ['Growing Businesses', 'Multi-site Operations', 'Specialized Industries']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#14173A] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37] to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37] mb-6">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Our Services</span>
          </div>
          
          <h1 className="text-6xl font-bold text-white mb-6">
            Practical Safety Solutions
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Tailored approaches to meet you where you are — from compliance foundations to advanced safety culture systems.
          </p>
          
          <div className="flex items-center justify-center gap-3 text-gray-400 text-sm">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#D4AF37]">Services</span>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 bg-[#F7F7F9]">
        <div className="max-w-6xl mx-auto px-6 space-y-16">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 w-2 h-full bg-[#D4AF37] transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
              
              <div className="relative">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <div className="text-7xl font-thin text-[#D4AF37] mb-4 leading-none">{service.number}</div>
                    <h2 className="text-3xl font-bold text-[#14173A] mb-4">{service.title}</h2>
                    <p className="text-xl text-[#D4AF37] italic font-medium mb-8">{service.value}</p>
                  </div>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-[#D4AF37] mb-4 uppercase tracking-wide">What's Included</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {service.included.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-[#14173A] mb-3">Best For</h3>
                  <p className="text-gray-600 mb-4">{service.bestFor}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.industries.map((industry, i) => (
                      <span 
                        key={i}
                        className="px-4 py-2 bg-[#14173A] text-white text-sm rounded-full"
                      >
                        {industry}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#14173A] rounded-2xl font-semibold hover:bg-[#C9A832] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Request Consultation
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#14173A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Not Sure Which Service Is Right for You?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Let's talk about your current safety challenges and goals. We'll help you find the right starting point.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#D4AF37] text-[#14173A] rounded-2xl font-bold text-lg hover:bg-[#C9A832] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Schedule a Free Consultation
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
